import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Button, Form, Toast } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import AdminMaster from '../../../Layout/admin/Master';
import { CLAIMS_FILING, NPI_AND_TIN_LIST } from '../../../routeNames';
import { changeAllTitleFunction, getRedirectTabId } from '../Dashboard/functions';
import { createOrUpdateNpiAndTin, getNpiData, getSingleNpiAndTinDetail } from '../../../Redux/npiandtin/action';
import ClientDropDown from '../../common/PartnerClientFacilityDropDown/ClientDropDown';
import FacilityDropDown from '../../common/PartnerClientFacilityDropDown/FacilityDropDown';
import { Tooltip } from '@mui/material';

import { MdOutlineLastPage } from "react-icons/md";
import { MdFirstPage } from "react-icons/md";
import { GrFormNext } from "react-icons/gr";
import { GrFormPrevious } from "react-icons/gr";
import Swal from 'sweetalert2';



// Function to generate random NPI (10 digits)
const generateRandomNPI = () => {
  return Math.floor(1000000000 + Math.random() * 9000000000).toString();
};

// Function to generate random TIN (9 digits)
const generateRandomTIN = () => {
  return Math.floor(100000000 + Math.random() * 900000000).toString();
};

// Function to generate initial 1000 TIN entries with NPIs
const generateInitial1000Entries = () => {
  const entries = [];
  for (let i = 0; i < 100; i++) {
    const npiCount = Math.floor(Math.random() * 5) + 1;
    const npiArray = [];
    for (let j = 0; j < npiCount; j++) {
      npiArray.push({ npi: generateRandomNPI() });
    }
    entries.push({
      tin: generateRandomTIN(),
      npi_array: npiArray
    });
  }
  return entries;
};

// Validation schema
const validationSchema = Yup.object().shape({
  clients: Yup.array()
    .of(
      Yup.mixed().test(
        'is-string-or-number',
        'Each client must be a string or number',
        value => typeof value === 'string' || typeof value === 'number'
      )
    )
    .min(1, 'Required')
    .required('Required'),

  facility: Yup.array()
    .of(
      Yup.mixed().test(
        'is-string-or-number',
        'Each facility must be a string or number',
        value => typeof value === 'string' || typeof value === 'number'
      )
    )
    .min(1, 'Required')
    .required('Required'),

  tin_array: Yup.array()
    .of(
      Yup.object().shape({
        tin: Yup.string()
          .required("Required")
          .matches(/^[0-9]+$/, "Must be only digits")
          .length(9, 'Must be exactly 9 digits'),

        npi_array: Yup.array().of(
          Yup.object().shape({
            npi: Yup.string()
              .required("Required")
              .matches(/^[0-9]+$/, "Must be only digits")
              .length(10, 'Must be exactly 10 digits'),
          })
        )
      })
    )
    .test(
      'at-least-one-tin-or-npi',
      'At least one TIN or one NPI is required',
      (tinArray) => {
        // console.log("tinArray",tinArray);
        
        if (!tinArray || tinArray.length === 0) return false;
        return tinArray.some(item => {
          const hasValidTin = item?.tin && /^[0-9]{9}$/.test(item.tin);
          const hasValidNpi = item?.npi_array?.some(n =>
            n?.npi && /^[0-9]{10}$/.test(n.npi)
          );
          return hasValidTin || hasValidNpi;
        });
      }
    )
});

// NPI Nested Array Component (like your nestedFieldArray)
const NpiNestedArray = ({ nestIndex, control, register, errors, trigger, setFocus, getValues, setValue }) => {
  const { fields, remove, append } = useFieldArray({
    control,
    name: `tin_array.${nestIndex}.npi_array`
  });

  const handleAddNpi = useCallback(() => {
    append({ id: null, npi: "" });

    // Trigger validation after adding
    setTimeout(() => {
      const newIndex = fields.length;
      trigger(`tin_array.${nestIndex}.npi_array.${newIndex}.npi`);
      setFocus(`tin_array.${nestIndex}.npi_array.${newIndex}.npi`);
    }, 0);
  }, [append, fields.length, trigger, setFocus, nestIndex]);

  const handleRemoveNpi = useCallback((npiIndex, npi_id = null) => {
    if (npi_id) {
      const currentValues = getValues();
      setValue('npi_removed_ids', [...(currentValues.npi_removed_ids || []), npi_id], { shouldDirty: true });
    }
    remove(npiIndex);

    // Trigger validation after removing
    setTimeout(() => {
      trigger(`tin_array.${nestIndex}.npi_array`);
    }, 0);
  }, [remove, trigger, nestIndex]);

  return (
    <div className="col-lg-8">
      {fields.map((item, npiIndex) => (
        <div key={item.id} className="d-flex justify-content-start gap-2 col-lg-12 pb-2">
          <div className="d-flex flex-column justify-content-start">
            {npiIndex === 0 && nestIndex === 0 && (
              <Form.Label className='fw-bold'>NPI</Form.Label>
            )}
            <div className='d-flex flex-row gap-2'>
              <div style={{ width: "200px" }}>
                <Form.Group>
                  <Form.Control
                    {...register(`tin_array.${nestIndex}.npi_array.${npiIndex}.npi`, {
                      onChange: (e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                        e.target.value = value;
                      }
                    })}
                    onBlur={() => trigger(`tin_array.${nestIndex}.npi_array.${npiIndex}.npi`)}
                    type="text"
                    autoComplete="one-time-code"
                    className='b-r-10 form-control-solid'
                    maxLength="10"
                    placeholder="NPI"
                    isInvalid={!!errors?.tin_array?.[nestIndex]?.npi_array?.[npiIndex]?.npi}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors?.tin_array?.[nestIndex]?.npi_array?.[npiIndex]?.npi?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>

              <Tooltip
                disableInteractive title={'Delete NPI'} placement='top' arrow
              >
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => handleRemoveNpi(npiIndex, item?.npi_id)}
                  disabled={fields.length <= 1}
                  type="button"
                >
                  <i className='bi bi-trash'></i>
                </Button>
              </Tooltip>

              {npiIndex === fields.length - 1 && (
                <Tooltip
                  disableInteractive title={'Add NPI'} placement='top' arrow
                >
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleAddNpi}
                    type="button"
                  >
                    <i className='bi bi-plus'></i>
                  </Button>
                </Tooltip>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// TIN Fields Component (like your Fields component)
const TinFields = ({ control, register, setValue, getValues, errors, trigger, setFocus ,limit,handleAddNew ,tinJson ,handleResetNpiTin,page,totalCount ,onPageChange ,setTotalCount ,loading,watch ,isValid ,blockApiRef ,showToast ,setTinJson ,state}) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: "tin_array"
  });

  const handleAddTin = useCallback((tinIndex) => {



  //  setTotalCount(prev=>prev+1)


    if (limit <= tinIndex + 1  ) {
     const updatedTinArray =  watch('tin_array')
    //  console.log(fields);
     
      if (JSON.stringify(updatedTinArray?.map(({ id, ...rest }) => ({ ...rest }))) !== tinJson) { //field Edited save befor switch 
      //  console.log(errors,"HHHHH");
       
        if (isValid) {
          Swal.fire({
            icon: 'info',
            title: 'Limit exceeded. Please save and continue.',
            showCancelButton: true,
            confirmButtonText: 'Save & Continue',
            cancelButtonText: 'Cancel',
            reverseButtons: true, // Cancel on the left, Confirm on the right
            confirmButtonColor: '#6ba2b6',  // Save button
            cancelButtonColor: '#6c757d',   // Cancel button (e.g., red)
            customClass: {
              title: 'swal-titleText'
            }
          }).then((result) => {
            if (result.isConfirmed) {
              setTinJson(JSON.stringify([]))
              handleAddNew()
            } else if (result.isDismissed) {

            }
          });
        } else {
          showToast('error', "Error  Fields");
        }

      } else {
        setTotalCount(prev=>prev+1)
        handleResetNpiTin({isBlocksetPage:false})
      }
      return
    }

    append({
      tin_id: null,
      tin: "",
      npi_array: [{ npi_id: null, npi: "" }]
    });
 setTotalCount(prev=>prev+1)
    // Trigger validation after adding
    setTimeout(() => {
      const newIndex = fields.length;
      if (newIndex === 1) {
        trigger(`tin_array.${0}`);
      }
      trigger(`tin_array.${newIndex}`);
    }, 0);
  }, [append, fields.length, trigger, setFocus,limit ,tinJson ,isValid]);



const handleRemoveTin = useCallback((tinIndex, tin_id = null) => {
  remove(tinIndex);
setTotalCount(prev=>prev-1)
  const currentValues = getValues();
  const updatedTinArray = [...(currentValues.tin_array || [])];
  // updatedTinArray.splice(tinIndex, 1); // remove the item from local copy

  // ✅ Track removed IDs if needed
  if (tin_id) {
    setValue(
      'tax_removed_ids',
      [...(currentValues.tax_removed_ids || []), tin_id],
      { shouldDirty: true }
    );
  }

  // ✅ If no items left, decrease page (only if > 1)
  if (updatedTinArray.length === 0 && page > 1) {
    
    // blockApiRef.current = true; this comment is for the save if the delete any of the npi or tin
    onPageChange(page - 1);
  }else{
 setTimeout(() => {
    trigger(`tin_array`);
  }, 0);
  }
  

  // ✅ Trigger validation after removal
 
}, [remove, trigger, getValues, setValue, page]);

  const isShowAddIcon = useMemo(() => {
    const lastPage = Math.ceil(totalCount / limit); // 9
    return page >= lastPage;
  }, [totalCount,limit,page,])
  // console.log(isShowAddIcon,"isShowAddIcon");
  
//   const isShowAddIcon =() => {
//    const lastPage = Math.ceil(totalCount / rowsPerPage); // 9
// return page === lastPage;
//   }

const currentpageLastItem = () => {
   const start = totalCount === 0 ? 0 : (page - 1) * limit + 1;
   return start+getValues('tin_array')?.length-1
}
console.log(currentpageLastItem() === totalCount ,"FFFF")

  return (
<div className="position-relative" style={{minHeight:'150px'}}>
   {(state?.loading||loading) && (
    <div className="loading-overlay d-flex justify-content-center align-items-center">
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Loading...</span>
    </div>
  </div>
  )}



    <div className='col-lg-12 npi-tin-container mt-3' style={{ maxHeight: 'calc(100vh - 400px)', overflowY: 'auto', overflowX: 'hidden' }}>
      <div className='d-flex flex-column gap-3'>
        {fields.map((item, tinIndex) => (
          <div key={item.id} className='d-flex justify-content-start gap-2 col-lg-12 pb-2'>
            <div className="col-lg-4 d-flex flex-column justify-content-start">
              {tinIndex === 0 && <Form.Label className='fw-bold'>TIN</Form.Label>}
              <div className='d-flex flex-row gap-2'>
                <div style={{ width: "200px" }}>
                  <Form.Group>
                    <Form.Control
                      {...register(`tin_array.${tinIndex}.tin`, {
                        onChange: (e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 9);
                          e.target.value = value;
                        }
                      })}
                      onBlur={() => trigger(`tin_array.${tinIndex}.tin`)}
                      type="text"
                      autoComplete="one-time-code"
                      className='b-r-10 form-control-solid'
                      maxLength="9"
                      placeholder="TIN"
                      isInvalid={!!errors?.tin_array?.[tinIndex]?.tin}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors?.tin_array?.[tinIndex]?.tin?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </div>

                <Tooltip
                  disableInteractive title={'Delete TIN'} placement='top' arrow
                >
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleRemoveTin(tinIndex, item?.tin_id)}
                    disabled={(!isShowAddIcon||page == 1)&& fields.length <= 1}
                    type="button"
                  >
                    <i className='bi bi-trash'></i>
                  </Button>
                </Tooltip>

                { currentpageLastItem() === totalCount && tinIndex === fields.length - 1 && (
                  <Tooltip
                    disableInteractive title={'Add TIN'} placement='top' arrow
                  >
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={()=>handleAddTin(tinIndex)}
                      type="button"
                    >
                      <i className='bi bi-plus'></i>
                    </Button>
                  </Tooltip>
                )}
              </div>
            </div>

            <NpiNestedArray
              nestIndex={tinIndex}
              control={control}
              register={register}
              errors={errors}
              trigger={trigger}
              setFocus={setFocus}
              getValues={getValues}
              setValue={setValue}
            />
          </div>
        ))}
      </div>
    </div>

    </div>
  );
};

// Custom dropdown wrapper for React Hook Form
const ControlledClientDropDown = ({ watch, trigger, control, name, ...props }) => (
  <Controller
    name={name}
    control={control}
    render={({ field, fieldState: { error } }) => (
      <div>
        <ClientDropDown
          lockboxformik={{
            values: { clients: watch('clients') || [] },
            setFieldValue: (fieldName, value) => {
              field.onChange(value);
              trigger(fieldName);
            },
            errors: error ? { clients: error?.message } : {},
            touched: { clients: true }
          }}
          isSingle={true}
          width='100%'
          isPartner={false}
          isDisabled={true}
          {...props}
        />
      </div>
    )}
  />
);

const ControlledFacilityDropDown = ({ trigger, watch, control, name, ...props }) => (
  <Controller
    name={name}
    control={control}
    render={({ field, fieldState: { error } }) => (
      <div>
        <FacilityDropDown
          lockboxformik={{
            values: { facility: field.value || [], clients: watch("clients") },
            setFieldValue: (fieldName, value) => {
              field.onChange(value);
              trigger(fieldName);
            },
            errors: error ? { facility: error.message } : {},
            touched: { facility: true }
          }}
          isSingle={true}
          width='100%'
          isDisabled={true}
          {...props}
        />
      </div>
    )}
  />
);

const PaginationWithIcons = ({
  page,
  count,
  onChange,
  limit,
  onRowsPerPageChange,
  totalCount,
  rowsPerPageOptions = [25, 50, 100],
  loaderDisable,
  getValues,
}) => {
  const isFirstPage = page === 1;
  const isLastPage = page === count;

  const start = totalCount === 0 ? 0 : (page - 1) * limit + 1;
  const end = Math.min(page * limit, totalCount);
  const handleNext = () => {
  let  max = Math.ceil(totalCount / limit)

    if (page + 1 > max) {
      onChange(max)

    } else {

      onChange(page + 1)
    }
  }
  
  

  return (
    <div className='row'>
    <div className='col-7' style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', justifyContent:'space-between'}}>
      {/* Rows per page selector */}
     
     
      {/* Showing data count */}
      <div>
        {`Showing ${start}-${start+getValues('tin_array')?.length-1} of ${totalCount}`}
      </div>


      <div className='d-flex gap-2'>
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <span>Rows per page:</span>
        <select
          value={limit}
          onChange={e => onRowsPerPageChange(Number(e.target.value))}
          style={{ padding: '4px 8px',
            outline:'none',
            border:'none'
           }}
        >
          {rowsPerPageOptions.map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      </div>


      {/* Pagination controls */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <button
          style={(isFirstPage|| loaderDisable) ? iconButtonDisabledStyle : iconButtonStyle}
          onClick={() => onChange(1)}
          disabled={isFirstPage ||loaderDisable}
          aria-label="first page"
          type='button'
        >
          <MdFirstPage />
        </button>
        <button
          style={(isFirstPage ||loaderDisable) ? iconButtonDisabledStyle : iconButtonStyle}
          onClick={() => onChange(page - 1)}
          disabled={isFirstPage ||loaderDisable}
          aria-label="previous page"
          type='button'
        >
          <GrFormPrevious />
        </button>
       
        <button
          style={(( totalCount === (start+getValues('tin_array')?.length-1))  ||loaderDisable)? iconButtonDisabledStyle : iconButtonStyle}
          onClick={handleNext}
          disabled={( totalCount === (start+getValues('tin_array')?.length-1)) ||loaderDisable}
          aria-label="next page"
          type='button'
        >
          <GrFormNext />
        </button>
        <button
          style={(( totalCount === (start+getValues('tin_array')?.length-1)) ||loaderDisable) ? iconButtonDisabledStyle : iconButtonStyle}
          onClick={() => onChange(count)}
          disabled={(( totalCount === (start+getValues('tin_array')?.length-1))) ||loaderDisable}
          aria-label="last page"
          type='button'
        >
          <MdOutlineLastPage />
        </button>
      </div>
      </div>

    </div>
    </div>
  );
};

const CreateNpiAndTin = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  // const [rowsPerPage, setRowsPerPage] = useState(10);
  const [limit, setLimit] = useState(100);
  const [totalCount,setTotalCount]  = useState(1);
  const [tinJson,setTinJson]  = useState(null);
  const blockApi = useRef(null)
  const blockRedirect = useRef(null)
  const pageCount = Math.ceil(totalCount / limit);
  const [loaderDisable,sestLoaderDisable] = useState(true)
  const [triggerState,setTriggerState] = useState(true)

  // Reset to first page if rowsPerPage changes
  const handleRowsPerPageChange = (newRows) => {
    const updatedTinArray = watch('tin_array')
    if (JSON.stringify(updatedTinArray?.map(({ id, ...rest }) => ({ ...rest }))) !== tinJson) {

      if (isValid) {
        Swal.fire({
          icon: 'info',
          title: 'Please save and continue.',
          showCancelButton: true,
          confirmButtonText: 'Save & Continue',
          cancelButtonText: 'Cancel',
          reverseButtons: true, // Cancel on the left, Confirm on the right
          confirmButtonColor: '#6ba2b6',  // Save button
          cancelButtonColor: '#6c757d',   // Cancel button (e.g., red)
          customClass: {
            title: 'swal-titleText'
          }
        }).then((result) => {
          if (result.isConfirmed) {
            const data1 = getValues()
            blockRedirect.current=true
            onSubmit(data1)
            setLimit(newRows);
            setPage(1);

          } else if (result.isDismissed) {

          }
        });
      } else {
        showToast('error', "Error  Fields");
        return
      }
    } else {

      setLimit(newRows);
      setPage(1);
    }

  }


  // Consolidated state
  const [state, setState] = useState({
    loading: false,
    error: null,
    removedIds: [],
    toast: {
      show: false,
      type: '',
      message: ''
    }
  });

  // Default values
  const defaultValues = useMemo(() => ({
    id: '',
    clients: [],
    facility: [],
    npi_removed_ids: [],
    tax_removed_ids: [],
    tin_array: [{
      id: null,
      tin: "",
      npi_array: [{
        id: null,
        npi: ""
      }],
    }],
  }), []);

  // React Hook Form setup
  const {
    trigger,
    setFocus,
    control,
    register,
    handleSubmit,
    setValue,
    watch,
    getValues,
    clearErrors,
    formState: { errors, isSubmitting, isValid }
  } = useForm({
    defaultValues,
    resolver: yupResolver(validationSchema),
    mode: 'onBlur',
    //  shouldUnregister: true
  });
//   const { fields: tinFields, append: appendTin, replace: replaceTin } = useFieldArray({
//   control,
//   name: "tin_array"
// });

  // Optimized toast handler
  const showToast = useCallback((type, message, duration = 3000) => {
    setState(prev => ({
      ...prev,
      toast: { show: true, type, message }
    }));

    setTimeout(() => {
      setState(prev => ({
        ...prev,
        toast: { show: false, type: '', message: '' }
      }));
    }, duration);
  }, []);

  // Form submission
  const onSubmit = async (data, updatedPageNum = null) => {
    // console.trace("HHHH")
    try {
      setState(prev => ({ ...prev, loading: true }));
      sestLoaderDisable(true)
      const redirectId = getRedirectTabId();
      const { id, clients, facility, ...rest } = data;
      const payload = {
        ...(id ? { id } : {}),
        ...rest,
        client: clients[0],
        facility: facility[0],
      };

      // Array.isArray(payload?.npi_removed_ids ) && payload?.npi_removed_ids?.length > 0 &&
      // Array.isArray(payload?.tin_array ) && payload?.tin_array?.length > 0 &&
      // Array.isArray(payload?.tax_removed_ids ) &&payload?.tax_removed_ids?.length > 0

   

      const response = await new Promise((resolve) => {
        createOrUpdateNpiAndTin(payload)(resolve);
      });


      if (response?.status && response?.status_code === 201) {
        showToast(
          'success',
          id ? 'Updated successfully!' : 'Created successfully!',
          1000
        );
          if (!blockRedirect.current) {

          setTimeout(() => {
            changeAllTitleFunction("Npi&Tin", NPI_AND_TIN_LIST, redirectId);
          }, 1000);
        }
        else {
          blockRedirect.current = false
          
          if (!!updatedPageNum) {
            setTriggerState(prev=>!prev)
            setPage(updatedPageNum)
          } else {
            sestLoaderDisable(false)
          }
        }
      

      } else {
        const errorMessage =
          response?.response?.data?.message || 'Something went wrong';
        showToast('error', errorMessage, 2000);
        sestLoaderDisable(false)
      }
    

      setValue("tax_removed_ids", [])
      setValue("npi_removed_ids", [])


    } catch (error) {
      // console.error(error);

      showToast('error', 'An unexpected error occurred', 2000);
    } finally {

      setState(prev => ({ ...prev, loading: false }));
    }
  };

// console.log(errors,"JJJJJJJ");

  // Load data for edit mode
  useEffect(() => {
    if (id) {
      const loadData = async () => {
        try {


          setState(prev => ({ ...prev, loading: true }));
          const response = await new Promise((resolve) => {
            getSingleNpiAndTinDetail({ id, page, limit })(resolve);
          });
          if (response?.status && response?.data) {


            setValue('id', response.data.id);
            setValue('clients', [response.data.client]);
            setValue('facility', response.data.facility ? [response.data.facility] : []);


          }
 setTimeout(() => {
        trigger(); // now RHF has fully registered the values
      }, 0);

        } catch (error) {
          // console.log(error);

          showToast('error', 'Failed to load data');
        } finally {
        
          setState(prev => ({ ...prev, loading: false }));
        }
      };
      loadData();
    } else {
      setTimeout(() => {
        trigger(); // now RHF has fully registered the values
      }, 0);
    }
  }, [id, setValue,]);
  

  useEffect(() => {
  if (id) {
    const loadDataTest = async () => {
      try {
        sestLoaderDisable(true); // Moved here: before the async operation starts

        if (!blockApi.current) {
          const NPIresponse = await new Promise((resolve) => {
           
              getNpiData({ id, page, limit })(resolve);
           
          });

          if (NPIresponse?.status) {
            let npiTinData =
              NPIresponse?.data?.results?.length > 0
                ? NPIresponse?.data?.results
                : [
                    {
                      id: null,
                      tin: "",
                      npi_array: [
                        {
                          id: null,
                          npi: "",
                        },
                      ],
                    },
                  ];

            setTotalCount(NPIresponse?.data?.count || 1);
           
              setValue("tin_array", npiTinData);
             
           
            setTinJson(JSON.stringify(npiTinData));
          }
        } else {
          setTimeout(() => {
            blockApi.current = false;
          }, 200);
        }
        setTimeout(() => {
          trigger(); // now RHF has fully registered the values
        }, 0);
      } catch (error) {
        // console.log(error);
        showToast("error", "Failed to load data");
      } finally {
        // Now it will run after everything completes
        sestLoaderDisable(false);
        setState((prev) => ({ ...prev, loading: false }));
      }
    };

    loadDataTest();
  }
}, [id, setValue, limit, page ,triggerState]);

  const handleCancel = useCallback(() => {
    changeAllTitleFunction("NPI & TIN", NPI_AND_TIN_LIST);
  }, []);
// console.log(getValues("tin_array"),"HHHHH");

  // Helper functions for testing
  const handleClearAll = useCallback(() => {
    setValue('tin_array', [{ tin: "", id: null, npi_array: [{ id: null, npi: "" }] }]);
  }, [setValue]);

  const handleRegenerate1000 = useCallback(() => {
    setValue('tin_array', generateInitial1000Entries());
  }, [setValue]);

  // Watch tin_array for count display
  // const watchedTinArray = watch('tin_array');
  // console.log(errors)
  // console.log(watchedTinArray)
 const handleResetNpiTin =  () => {

  setValue('tin_array', [{ tin: "", id: null, npi_array: [{ id: null, npi: "" }] }]);
    //no need if handleAddnew
   
      setPage(prev=>prev+1)
  
}

  const handleAddNew = () => {
    // console.log(errors, isSubmitting, isValid);
    if (isValid) {

      const data1 = getValues()
      // console.log(data1);
      blockRedirect.current=true //for block the redirect page after submit 
      onSubmit(data1)
      setTotalCount(prev=>prev+1)
      blockApi.current = true
      handleResetNpiTin({isBlocksetPage:true})
    }
  }
  const onPageChange = (updatedPageNum) => {
    if(isValid){

      // setPage(updatedPageNum)
      const tinArray = watch('tin_array')
      if (JSON.stringify(tinArray.map(({ id, ...rest }) => ({ ...rest }))) !== tinJson) {
        const data1 = getValues()
        clearErrors()
        trigger()
        blockRedirect.current=true //for block the redirect page after submit 
        onSubmit(data1 ,updatedPageNum)
      }
      else{
        setPage(updatedPageNum)
      }
    }else{
      showToast('error', "Error  Fields");
      trigger()
    }
  }
  // console.log(errors,"GGGGGGG")
 
  return (
    <React.Fragment>
      <AdminMaster
        ToastMessage={state.toast.show ? state.toast.type : false}
        message={state.toast.message}
      />

      <main id="main" className="main">
        <div className="pagetitle tab-fixed-header">
          <h1>{id ? 'Edit NPI & TIN' : 'Create NPI & TIN'}</h1>
        </div>

        <section className="section mt-5">
          <div className="card card-flush py-4">
            <Form onSubmit={handleSubmit(onSubmit)}>
              <div className='px-4 py-2'>
                {/* Client and Facility Selection */}
                <div className="d-flex flex-wrap gap-3 mb-4">
                  <div className='col-lg-4'>
                    <Form.Label className='fw-bold'>Client</Form.Label>
                    <ControlledClientDropDown
                      control={control}
                      name="clients"
                      trigger={trigger}
                      watch={watch}
                    />
                  </div>
                  <div className='col-lg-4'>
                    <Form.Label className='fw-bold'>Facility</Form.Label>
                    <ControlledFacilityDropDown
                      control={control}
                      name="facility"
                      watch={watch}
                      trigger={trigger}
                    />
                  </div>
                </div>

                {/* Load Test Controls */}
                {/* <div className="d-flex gap-2 mb-3 p-3 bg-light rounded">
                  <Button
                    variant="warning"
                    size="sm"
                    onClick={handleRegenerate1000}
                    disabled={isSubmitting}
                    type="button"
                  >
                    Regenerate 1000 Entries
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={handleClearAll}
                    disabled={isSubmitting}
                    type="button"
                  >
                    Clear All
                  </Button>
                  <div className="ms-auto">
                    <strong>Total TIN Entries: {watchedTinArray?.length || 0}</strong>
                  </div>
                </div> */}

                {/* TIN Array using nested field arrays */}
                <TinFields
                  control={control}
                  register={register}
                  setValue={setValue}
                  getValues={getValues}
                  errors={errors}
                  trigger={trigger}
                  setFocus={setFocus}
                  limit={limit}
                  handleAddNew={handleAddNew}
                  tinJson={tinJson}
                  handleResetNpiTin={handleResetNpiTin}
                  page={page}
                  totalCount={totalCount}
                  blockApiRef={blockApi}
                  onPageChange = {onPageChange}
                  setTotalCount={setTotalCount}
                  loading={loaderDisable}
                  watch={watch}
                  isValid={isValid}
                  showToast={showToast}
                  setTinJson={setTinJson}
                  state={state}
                  
                  
                  

                />
                <PaginationWithIcons
                  page={page}
                  count={pageCount}
                  onChange={onPageChange}
                  limit={limit}
                  onRowsPerPageChange={handleRowsPerPageChange}
                  totalCount={totalCount}
                  loaderDisable={loaderDisable}
                  getValues={getValues}
                />
                {/* Action Buttons */}
                <div className='d-flex justify-content-end gap-2 mt-4'>
                  <Button
                    variant="secondary"
                    onClick={handleCancel}
                    disabled={isSubmitting}
                    type="button"
                  >
                    Cancel
                  </Button>
                  <button
                    type="submit"
                    className="btn btn-light btn-active-primary"
                    disabled={!isValid || isSubmitting || state.loading}
                  >
                    {isSubmitting ?
                      (id ? 'Updating...' : 'Creating...') :
                      (id ? 'Update' : 'Create')
                    }
                  </button>
                </div>
              </div>
            </Form>
          </div>
        </section>
      </main>
    </React.Fragment>
  );
};

export default CreateNpiAndTin;






const iconButtonStyle = {
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  padding: '8px',
  fontSize: '20px',
  color: '#1976d2',
  display: 'flex',
  alignItems: 'center',
  opacity: 1,
  transition: 'opacity 0.2s',
};

const iconButtonDisabledStyle = {
  ...iconButtonStyle,
  cursor: 'not-allowed',
  opacity: 0.5,
};